import React, { useState, useEffect } from 'react'
import theme from '@app/ui/theme'
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import sv from 'date-fns/locale/sv'
import { ButtonProps } from 'react-native'
import * as S from './styled'
import { useAxios } from '@common/hooks/useAxios'
import {
  ProjectCourse,
  ProjectHole,
  ProjectTee,
  ProjectTeeHole,
} from '../../../types/Project'
import { shadeColor } from '@app/evaluation-core/evaluation'
import ScorecardTotalRow from './ScorecardTotalRow'
import ScorecardRow from './ScorecardRow'

import {
  CourseOptionSelect,
  CourseOptionSelectItem,
} from '@app/evaluation-settings-menu/HeaderArea.styles'
import { RatingTemplate } from '../../../types/Templates'

registerLocale('sv', sv)

type Props = {
  courseId?: number
  branchId?: string
  projectCourse?: Partial<ProjectCourse>
  holeIds?: string[]
  isComposite?: boolean
  template?: RatingTemplate
  units?: 'metric' | 'imperial'
  developerMode?: boolean
}

const metersInOneYard = 0.9144

export const convertToUnits = (
  length: number,
  units: 'metric' | 'imperial'
): number => {
  return units === 'imperial'
    ? Math.round(length / metersInOneYard)
    : Math.round(length)
}

export const parseColor = (color: string) => {
  if (color.startsWith('#') && color.length === 9)
    return `#${color.substring(3)}`

  return color
}

const CourseScorecard: React.FC<Props> = ({
  courseId,
  branchId,
  projectCourse,
  holeIds,
  isComposite = false,
  template,
  units = 'metric',
  developerMode = false,
}) => {
  if (
    (!courseId || courseId === null) &&
    (!projectCourse || projectCourse === null) &&
    (!branchId || branchId === null)
  )
    return <>Loading</>

  const axios = useAxios('/courses')
  const [course, setCourse] = useState<Partial<ProjectCourse>>()

  const refreshScorecard = async () => {
    if (branchId) {
      const { data } = await axios.get<{
        review: number
        tees: {
          id: string
          teeIndex: number
          name: string
          teeNumber: number
        }[]
        branchHoles: ProjectHole[]
      }>(`/scorecard/${branchId}?holeIds=${holeIds?.join(',') ?? ''}`)

      const teesOnHoleMaxLength = data.branchHoles
        .map((b) => b.teesOnHole.length)
        .reduce((acc, val) => Math.max(acc, val), 0)

      const filterDuplicates = (array: any[]) => {
        return array
          .filter(
            (value, index, self) =>
              index === self.findIndex((t) => t.teeIndex === value.teeIndex)
          )
          .slice(0, teesOnHoleMaxLength)
      }

      setCourse({
        name: template?.name,
        holes: isComposite
          ? data.branchHoles.map((hole, index) => ({
              ...hole,
              originalHoleNumber: hole.courseHoleNumber,
              courseHoleNumber: (index + 1).toString(),
            }))
          : data.branchHoles,
        tees: filterDuplicates(data.tees),
      })
    } else {
      setCourse(await axios.get(`/scorecard/${courseId}`))
    }
  }

  useEffect(() => {
    if (projectCourse) setCourse(projectCourse)
    else {
      refreshScorecard()
    }
  }, [courseId, projectCourse])

  const editMode = false

  return (
    <S.ScorecardContainer>
      <S.TableWrapper>
        <table className="table-auto" style={{ borderSpacing: 0 }}>
          <thead style={{ fontWeight: 600 }}>
            <tr>
              <th></th>
              <th></th>
              <th></th>
              {developerMode && <th></th>}
              {course?.tees &&
                course?.tees.map((tee: ProjectTee) => (
                  <th key={`tee-head-${tee.id}`} colSpan={3}>
                    <div
                      className="statusColor"
                      style={{
                        display: 'inline-block',
                        backgroundColor: parseColor(tee.color),
                        border: `1px solid ${shadeColor(tee.color, -24)}`,
                        height: 24,
                        width: 24,
                        borderRadius: 50,
                        verticalAlign: 'middle',
                        marginRight: 8,
                        marginTop: 6,
                        marginBottom: 6,
                      }}
                    />
                    {tee.name}
                  </th>
                ))}
            </tr>
            <tr>
              <S.TitleTableCell style={{ verticalAlign: 'bottom' }}>
                Hole
              </S.TitleTableCell>
              <S.TitleTableCell style={{ verticalAlign: 'bottom' }}>
                Par
              </S.TitleTableCell>
              <S.TitleTableCell style={{ verticalAlign: 'bottom' }}>
                Hcp
              </S.TitleTableCell>
              {developerMode && (
                <S.TitleTableCell style={{ verticalAlign: 'bottom' }}>
                  Physical ID
                </S.TitleTableCell>
              )}
              {course?.tees &&
                course?.tees.map((tee: ProjectTee) => (
                  <React.Fragment key={`tee-cols-${tee.id}`}>
                    <S.RatedTableCell
                      style={{ height: 64, verticalAlign: 'bottom' }}
                    >
                      <S.LengthTitle>Rated</S.LengthTitle>
                    </S.RatedTableCell>
                    <S.FixedTableCell
                      style={{ height: 64, verticalAlign: 'bottom' }}
                    >
                      <S.LengthTitle>Fixed</S.LengthTitle>
                    </S.FixedTableCell>
                    <S.ScorecardTableCell
                      style={{ height: 64, verticalAlign: 'bottom' }}
                    >
                      <S.LengthTitle>Defined</S.LengthTitle>
                    </S.ScorecardTableCell>
                  </React.Fragment>
                ))}
            </tr>
          </thead>
          <tbody style={{ fontWeight: 300 }}>
            {course?.holes &&
              course?.holes.map((hole: ProjectHole) => {
                return (
                  <React.Fragment key={`sr-hole-${hole.id}`}>
                    <ScorecardRow
                      editMode={editMode}
                      hole={hole}
                      units={units}
                      developerMode={developerMode}
                    />
                    {Number(hole.courseHoleNumber) === 9 && (
                      <ScorecardTotalRow
                        course={course}
                        editMode={editMode}
                        hole={hole}
                        projectCourse={projectCourse}
                        units={units}
                        developerMode={developerMode}
                        sumType="out"
                        startHole={1}
                        endHole={9}
                      />
                    )}
                    {Number(hole.courseHoleNumber) > 9 &&
                      Number(hole.courseHoleNumber) ===
                        course?.holes?.length && (
                        <ScorecardTotalRow
                          course={course}
                          editMode={editMode}
                          hole={hole}
                          projectCourse={projectCourse}
                          units={units}
                          developerMode={developerMode}
                          sumType="in"
                          startHole={10}
                          endHole={18}
                        />
                      )}
                    {Number(hole.courseHoleNumber) > 9 &&
                      Number(hole.courseHoleNumber) ===
                        course?.holes?.length && (
                        <ScorecardTotalRow
                          course={course}
                          editMode={editMode}
                          hole={hole}
                          projectCourse={projectCourse}
                          units={units}
                          developerMode={developerMode}
                          sumType="out"
                          startHole={1}
                          endHole={9}
                        />
                      )}
                    {Number(hole.courseHoleNumber) ===
                      course?.holes?.length && (
                      <ScorecardTotalRow
                        course={course}
                        editMode={editMode}
                        hole={hole}
                        projectCourse={projectCourse}
                        units={units}
                        developerMode={developerMode}
                        sumType="tot"
                        startHole={1}
                        endHole={course?.holes?.length}
                      />
                    )}
                  </React.Fragment>
                )
              })}
          </tbody>
        </table>
      </S.TableWrapper>
    </S.ScorecardContainer>
  )
}

export default CourseScorecard
